import React, { useEffect, useState, useRef, memo } from 'react';
import '../css/skeleton.css';

// Helper component that prevents re-renders of the embed content
// This is crucial because 3rd party scripts modify the DOM (replacing blockquote with iframe)
// If React re-renders, it would try to put the blockquote back, breaking the embed.
const StaticEmbed = memo(({ children }) => {
  return <>{children}</>;
}, () => true);

const EmbedWrapper = ({ children, width = '380px', height = '691px', style = {}, className = '' }) => {
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    // Observer to detect when the script injects the iframe
    const observer = new MutationObserver((mutations) => {
      if (containerRef.current) {
        // Check for iframe in the container
        const iframe = containerRef.current.querySelector('iframe');
        if (iframe) {
          setLoading(false);
          observer.disconnect();
        }
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true });
    }

    // Fallback: check immediately in case script ran fast
    if (containerRef.current && containerRef.current.querySelector('iframe')) {
      setLoading(false);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={`video-wrapper ${className}`}
      style={{
        width, // Enforce width
        maxWidth: width,
        height: height, // Enforce height to match skeleton and reserve space
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: loading ? '#f0f0f0' : 'transparent', // Optional placeholder bg
        borderRadius: '3px',
        textAlign: 'center',
        ...style
      }}
    >
      {loading && (
        <div
          className="skeleton"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 10,
            borderRadius: '3px'
          }}
        />
      )}
      <StaticEmbed>{children}</StaticEmbed>
    </div>
  );
};

function Counter() {
  useEffect(() => {
    // Load TikTok embed script
    let tiktokScript = null;

    try {
      // Load TikTok embed script
      tiktokScript = document.createElement('script');
      tiktokScript.src = 'https://www.tiktok.com/embed.js';
      tiktokScript.async = true;

      // Add error handling
      tiktokScript.onerror = () => {
        console.warn('TikTok embed script failed to load. Check ad blockers.');
        // Optionally load a fallback
      };

      document.body.appendChild(tiktokScript);
    } catch (err) {
      console.error('Error loading TikTok script:', err);
    }

    // Load Instagram embed script
    const instaScript = document.createElement('script');
    instaScript.src = '//www.instagram.com/embed.js';
    instaScript.async = true;
    document.body.appendChild(instaScript);

    return () => {
      // Cleanup scripts on unmount
      if (tiktokScript.parentNode) {
        document.body.removeChild(tiktokScript);
      }
      if (instaScript.parentNode) {
        document.body.removeChild(instaScript);
      }
    };
  }, []);


  return (
    <section className="counter-area counter-bg">
      <div className="container-fluid" style={{ maxWidth: '1800px' }}>
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-8">
            <div className="counter-title text-center mb-65">
              <h6 className="sub-title">Nos Modèles Défilent !</h6>
              <h2 className="title">Voyez nos Collections en Vidéo.</h2>
            </div>
          </div>
        </div>
        <div className="social-videos-container" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: '15px',
          flexWrap: 'nowrap',
          overflow: 'visible'
        }}>
          {/* Instagram Video 1 - Kaki */}
          <EmbedWrapper className="first-instagram-video">
            <div className="video-handle" style={{
              marginBottom: '10px',
              fontWeight: 'bold',
              color: '#083142',
              fontSize: '16px'
            }}>
            </div>
            <blockquote
              className="instagram-media"
              data-instgrm-permalink="https://www.instagram.com/reel/DRz9ZtpDCk_/?utm_source=ig_embed&amp;utm_campaign=loading"
              data-instgrm-version="14"
              style={{
                background: '#FFF',
                border: 0,
                borderRadius: '3px',
                boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                margin: '1px',
                width: '380px', // Maintain internal width
                minWidth: '380px',
                maxWidth: '380px'
              }}
            >
            </blockquote>
          </EmbedWrapper>

          {/* Instagram Video 2 */}
          <EmbedWrapper style={{ marginRight: '25px' }} className="mobile-hidden">
            <div className="video-handle" style={{
              marginBottom: '10px',
              fontWeight: 'bold',
              color: '#083142',
              fontSize: '16px'
            }}>
            </div>
            <blockquote
              className="instagram-media"
              data-instgrm-permalink="https://www.instagram.com/reel/DR2jd-pDLFz/?utm_source=ig_embed&amp;utm_campaign=loading"
              data-instgrm-version="14"
              style={{
                background: '#FFF',
                border: 0,
                borderRadius: '3px',
                boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                margin: '1px',
                width: '380px',
                minWidth: '380px',
                maxWidth: '380px'
              }}
            >
            </blockquote>
          </EmbedWrapper>

          {/* TikTok Video 1 */}
          <EmbedWrapper className="mobile-hidden">
            <div className="video-handle" style={{
              marginBottom: '10px',
              fontWeight: 'bold',
              color: '#083142',
              fontSize: '16px'
            }}>
            </div>
            <blockquote
              className="tiktok-embed"
              cite="https://www.tiktok.com/@kaki_tn/video/7580399022226148615" data-video-id="7580399022226148615"
              style={{
                maxWidth: '380px',
                minWidth: '380px',
                width: '380px',
                margin: 0
              }}
            >
              <section></section>
            </blockquote>
          </EmbedWrapper>

          {/* TikTok Video 2 */}
          <EmbedWrapper className="mobile-hidden">
            <div className="video-handle" style={{
              marginBottom: '10px',
              fontWeight: 'bold',
              color: '#083142',
              fontSize: '16px'
            }}>
            </div>
            <blockquote
              className="tiktok-embed"
              cite="https://www.tiktok.com/@kaki_tn/video/7581866591671930119"
              data-video-id="7581866591671930119"
              style={{
                maxWidth: '380px',
                minWidth: '380px',
                width: '380px',
                margin: 0
              }}
            >
              <section></section>
            </blockquote>
          </EmbedWrapper>
        </div>
      </div>
    </section>

  )
}

export default Counter;
